import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { studentDetail } from "@/lib/studentsApi";

const fmt = (d) => (d ? new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

function AiBadge({ ai }) {
  const on = ai?.status === "connected";
  return <span data-testid="ai-status" className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${on ? "bg-emerald-100 text-emerald-800" : "bg-secondary text-muted-foreground"}`}>{on ? "connected" : "not connected"}</span>;
}

function DetailRow({ id }) {
  const [d, setD] = useState(null);
  useEffect(() => { studentDetail(id).then(setD).catch(() => setD({ error: true })); }, [id]);
  if (!d) return <tr><td colSpan={7} className="px-3 py-3 text-xs text-muted-foreground">Loading…</td></tr>;
  if (d.error) return <tr><td colSpan={7} className="px-3 py-3 text-xs text-red-600">Could not load detail.</td></tr>;
  const ai = d.student.ai || {};
  return (
    <tr className="bg-secondary/40" data-testid="student-detail-row">
      <td colSpan={7} className="px-3 py-3">
        <div className="grid gap-4 text-xs md:grid-cols-3">
          <div><div className="mb-1 font-mono uppercase tracking-widest text-muted-foreground">AI link</div>
            <div>AI user id: <span className="font-mono">{ai.user_id || "—"}</span></div><div>Connected: {fmt(ai.connected_at)} {ai.result ? `(${ai.result})` : ""}</div>
            <div>Assessment started: {fmt(ai.assessment_started_at)}</div><div>Assessment completed: {fmt(ai.assessment_completed_at)}</div><div>Last active: {fmt(ai.last_active_at)}</div></div>
          <div><div className="mb-1 font-mono uppercase tracking-widest text-muted-foreground">Source</div>
            <div className="truncate">Page: {d.student.source?.page_url || "—"}</div><div>UTM: {[d.student.source?.utm_source, d.student.source?.utm_medium, d.student.source?.utm_campaign].filter(Boolean).join(" / ") || "—"}</div>
            <div>Class: {d.student.class_level || "—"} · State: {d.student.state || "—"}</div><div>Consent: {d.student.whatsapp_consent ? "yes" : "no"} · Subscribed {d.student.subscription_count}×</div></div>
          <div><div className="mb-1 font-mono uppercase tracking-widest text-muted-foreground">Recent events</div>
            <ul className="space-y-0.5">{d.events.slice(0, 8).map((e) => <li key={e._id}>{fmt(e.ts)} · {e.event}</li>)}{d.events.length === 0 && <li>—</li>}</ul></div>
        </div>
      </td>
    </tr>
  );
}

export function StudentsTable({ data }) {
  const [open, setOpen] = useState(null);
  if (!data) return <div className="h-40 animate-pulse rounded-xl bg-secondary" />;
  return (
    <div className="overflow-x-auto rounded-xl border bg-card" data-testid="students-table">
      <table className="w-full text-sm">
        <thead className="bg-secondary/60 text-left text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
          <tr><th className="px-3 py-2">Student ID</th><th className="px-3 py-2">Name</th><th className="px-3 py-2">Contact</th><th className="px-3 py-2">Exams</th><th className="px-3 py-2">AI</th><th className="px-3 py-2">Subscribed</th><th className="px-3 py-2" /></tr>
        </thead>
        <tbody>
          {data.items.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground" data-testid="students-empty">No students match.</td></tr>}
          {data.items.map((s) => (
            <>
              <tr key={s.student_id} className="border-t hover:bg-secondary/30" data-testid={`student-row-${s.student_id}`}>
                <td className="px-3 py-2 font-mono text-xs">{s.student_id}</td>
                <td className="px-3 py-2 font-medium">{s.name}</td>
                <td className="px-3 py-2 text-xs"><div>{s.email}</div><div className="text-muted-foreground">{s.whatsapp}{s.whatsapp_consent ? "" : " · no consent"}</div></td>
                <td className="px-3 py-2"><div className="flex max-w-xs flex-wrap gap-1">{s.exams.map((e) => <span key={e} className="rounded-full border px-2 py-0.5 text-[11px]">{e}</span>)}</div></td>
                <td className="px-3 py-2"><AiBadge ai={s.ai} /></td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{fmt(s.created_at)}</td>
                <td className="px-3 py-2"><button data-testid={`student-toggle-${s.student_id}`} onClick={() => setOpen(open === s.student_id ? null : s.student_id)} className="rounded-md border p-1 hover:bg-secondary">{open === s.student_id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}</button></td>
              </tr>
              {open === s.student_id && <DetailRow key={`${s.student_id}-d`} id={s.student_id} />}
            </>
          ))}
        </tbody>
      </table>
    </div>
  );
}
